import React from "react";
import { Grid, Typography, Box, Alert } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import type { Plan, ProductPlatform } from "client/api-v2";
import { ENTITLEMENT_FIELD_ORDER } from "../../constants/entitlement-field-order";
import type { PlanWithConfigurableInclusions } from "../../types/plan-extensions";
import { getAllPlanInclusions, getInclusionValue } from "../../utils/plan-utils";
import { EntitlementField } from "./EntitlementField";

interface EntitlementEditorProps {
  selectedPlan?: Plan | PlanWithConfigurableInclusions;
  selectedPlatform?: ProductPlatform;
  disabled?: boolean;
}

export function EntitlementEditor({ selectedPlan, selectedPlatform, disabled }: EntitlementEditorProps) {
  const { control } = useFormContext();
  const watchedPlan = useWatch({ control, name: "planId" });

  // If no plan is selected, show a message
  if (!selectedPlan && !watchedPlan) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">Select a plan to configure entitlements</Typography>
      </Box>
    );
  }

  // Get all plan inclusions dynamically from the plan
  const planInclusions = getAllPlanInclusions(selectedPlan);

  if (planInclusions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No entitlements are defined for this plan.</Alert>
      </Box>
    );
  }

  // Sort inclusions by the priority order, with unknown fields at the end
  const fieldOrder: readonly string[] = ENTITLEMENT_FIELD_ORDER;
  const sortedInclusions = [...planInclusions].sort((a, b) => {
    const indexA = fieldOrder.indexOf(a);
    const indexB = fieldOrder.indexOf(b);

    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Entitlements
      </Typography>
      {selectedPlan && !("configurableInclusions" in selectedPlan) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These are the fixed entitlements included for the plan tier and cannot be modified.
        </Alert>
      )}
      <Grid container spacing={2}>
        {sortedInclusions.map((inclusionKey) => {
          const value = getInclusionValue(selectedPlan?.planInclusions, inclusionKey);

          // Determine grid size based on field type
          let gridSize = 6;
          if (typeof value === "boolean") {
            gridSize = 12; // Boolean fields take full width
          }

          return (
            <Grid
              key={inclusionKey}
              size={{
                xs: 12,
                sm: gridSize,
              }}
            >
              <EntitlementField fieldKey={inclusionKey} value={value} plan={selectedPlan} disabled={disabled} />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
